"use client";

import { useContext, useRef, Children, useState, useEffect, forwardRef } from "react";
import EdenContextualMenu from "@churchofjesuschrist/eden-contextual-menu";
import styled from "styled-components";
import { ArrowDropDown } from "@churchofjesuschrist/eden-icons";
import { sharedInputCss, internalUseOnlyOpenDetailsStyles } from "../utils/sharedCss";
import { spacing8, spacing4 } from "@churchofjesuschrist/eden-style-constants";
import { ValidityStateContext, SmallContext, IdContext, DescribedByContext, DisabledContext } from "../utils/context";
import Label from "./Label";
import Checkbox from "./Checkbox";
import EdenFieldSet from "./FieldSet";
import { AppearanceContext } from "@churchofjesuschrist/eden-appearance";
import { useHasOverflow } from "@churchofjesuschrist/eden-has-overflow";
import { mergeRefs } from "@churchofjesuschrist/eden-utilities";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const InternalUseOnlySelect = styled.select.withConfig({
  componentId: "sc-11vq36o-0"
})(["", ";margin:0;appearance:none;padding:", ";", ";", ";&:disabled{opacity:1;}"], sharedInputCss, ({
  $small
}) => $small ? `${spacing4} ${spacing8}` : spacing8, internalUseOnlyOpenDetailsStyles, ({
  $hasOverflow,
  multiple
}) => $hasOverflow && multiple ? `
    overflow: hidden;
    & > option {
      pointer-events: none;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    & > option:not(:first-of-type) {
      visibility: hidden;
    }
    ` : multiple ? `
    min-width: 14ch;
    ` : "");
const ContextualMenu = styled(EdenContextualMenu).withConfig({
  componentId: "sc-11vq36o-1"
})(["max-height:275px;min-width:200px;max-width:100%;"]);
const FieldSet = styled(EdenFieldSet).withConfig({
  componentId: "sc-11vq36o-2"
})(["& > div{width:100%;}"]);

/**
 * The `Select` component allows users to choose a single option from a list of options that appears when the form is activated.
 * @param {object} props - The component props
 * @param {string} [props.aria-describedby] - Identifies the element (or elements) that describes the Select
 * @param {React.ReactNode} props.children - Content to render inside the Select Component. Children should be <option> elements.
 * @param {string | string[]} [props.defaultValue] - Default value for uncontrolled use. If `multiple` is being used, this should be an array of values.
 * @param {boolean} [props.disabled] - Makes the input not mutable, focusable, or even submitted with the form
 * @param {string} [props.id] - An identifier which must be unique in the whole document
 * @param {boolean} [props.multiple] - Indicates that multiple options can be selected in the list
 * @param {string} [props.name] - Used to reference the input in JavaScript, or to reference form data after a form is submitted
 * @param {Function} [props.onChange] - Callback to execute when the value changes
 * @param {boolean} [props.required] - A boolean indicating if the Select should be rendered in a smaller height
 * @param {boolean} [props.small] - A boolean indicating if the Select should be rendered in a smaller height
 * @param {string | string[]} [props.value] - The value of the Select for controlled use. If `multiple` is being used, this should be an array of values.
 * @param {React.Ref} [forwardedRef] - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The Select component
 */
const SelectComponent = ({
  "aria-describedby": ariaDescribedBy,
  children,
  defaultValue,
  id,
  multiple,
  small,
  value,
  disabled,
  ...props
}, forwardedRef) => {
  const validityStateContext = useContext(ValidityStateContext);
  const smallContext = useContext(SmallContext);
  const idContext = useContext(IdContext);
  const describedByContext = useContext(DescribedByContext);
  const curAppearanceContext = useContext(AppearanceContext);
  const disabledContext = useContext(DisabledContext);
  const internalRef = useRef();
  const ref = forwardedRef ?? internalRef;
  const [open, setOpen] = useState(false);
  const fieldSetRef = useRef(null);
  const [activeChild, setActiveChild] = useState(1);
  const [optionValues, setOptionValues] = useState(value ?? defaultValue ?? []);
  const [overflowRef, {
    hasY
  }] = useHasOverflow(children, {
    startY: true
  });
  const hasOverflow = hasY && multiple;
  useEffect(() => {
    const closestForm = ref.current.form;
    if (!closestForm) {
      return;
    }
    const resetHandler = () => {
      setOptionValues(value ?? defaultValue ?? []);
    };
    closestForm.addEventListener("reset", resetHandler);
    return () => closestForm.removeEventListener("reset", resetHandler);
  });
  useEffect(() => {
    setOptionValues(value ?? defaultValue ?? []);
  }, [value, defaultValue]);
  useEffect(() => {
    if (open === true) {
      const fieldSet = fieldSetRef.current;
      if (!fieldSet) {
        return;
      }
      const firstChecked = fieldSet.querySelector(`input:checked`);
      const animationFrame = requestAnimationFrame(() => {
        if (firstChecked) {
          firstChecked.focus();
          setActiveChild(Array.from(firstChecked.parentNode.parentNode.children).indexOf(firstChecked.parentNode) + 1);
        } else {
          fieldSet.querySelector(`label:nth-child(1)`).focus();
          setActiveChild(1);
        }
      });
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [open]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(InternalUseOnlySelect, {
      "aria-describedby": ariaDescribedBy ? ariaDescribedBy : describedByContext,
      defaultValue: defaultValue,
      id: id ? id : idContext,
      multiple: multiple,
      ref: multiple ? mergeRefs(overflowRef, ref) : ref,
      $small: small ? small : smallContext,
      disabled: disabled ? disabled : disabledContext,
      $validityState: validityStateContext,
      $icon: /*#__PURE__*/_jsx(ArrowDropDown, {}),
      value: value,
      $curAppearanceContext: curAppearanceContext,
      $hasOverflow: hasOverflow,
      ...(hasOverflow ? {
        onMouseDown: event => {
          event.preventDefault();
        },
        onClick: event => {
          event.preventDefault();
          setOpen(true);
        },
        onKeyDown: event => {
          if (event.code === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
          if (event.code === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
          if (event.code === "Space") {
            event.preventDefault();
            setOpen(true);
          }
        },
        onScroll: event => {
          event.preventDefault();
          event.target.scrollTop = 0;
        }
      } : {}),
      ...props,
      children: [hasOverflow ? /*#__PURE__*/_jsx("option", {
        value: "eden-multiple-select-display-value",
        children: Children.toArray(children).filter(child => {
          if (typeof child.props.value !== "undefined") {
            return optionValues.includes(child.props.value.toString());
          } else {
            return optionValues.includes(child.props.children);
          }
        }).map(child => child.props.children || child.props.value).join(", ")
      }) : null, children]
    }), hasOverflow && ref.current ? /*#__PURE__*/_jsx(ContextualMenu, {
      forRef: ref,
      style: {
        width: ref.current.offsetWidth + "px"
      },
      open: open,
      onClickOutside: () => {
        setOpen(false);
      },
      onKeyDown: event => {
        const fieldSet = fieldSetRef.current;
        if (event.code === "ArrowDown") {
          event.preventDefault();
          if (activeChild < fieldSet.querySelectorAll("label").length) {
            const newActiveChild = activeChild + 1;
            fieldSet.querySelector(`label:nth-child(${newActiveChild})`).focus();
            setActiveChild(newActiveChild);
          }
        }
        if (event.code === "ArrowUp") {
          event.preventDefault();
          if (activeChild > 1) {
            const newActiveChild = activeChild - 1;
            fieldSet.querySelector(`label:nth-child(${newActiveChild})`).focus();
            setActiveChild(newActiveChild);
          }
        }
        if (event.code === "Escape") {
          event.preventDefault();
          setOpen(false);
          ref.current.focus();
        }
        if (event.code === "Enter") {
          event.preventDefault();
          fieldSet.querySelector(`label:nth-child(${activeChild})`).click();
        }
        if (event.code === "Tab") {
          setOpen(false);
        }
      },
      children: /*#__PURE__*/_jsx(FieldSet, {
        ref: fieldSetRef,
        children: Children.toArray(children).map(({
          props
        }, index) => {
          const childValue = props.value.toString() || props.children;
          return /*#__PURE__*/_jsxs(Label, {
            children: [props.children || childValue, /*#__PURE__*/_jsx(Checkbox, {
              disabled: props.disabled,
              tabIndex: "-1",
              checked: optionValues.includes(childValue),
              onChange: event => {
                setActiveChild(index + 1);
                const option = [...(ref.current?.options ?? [])].find(o => o.value === childValue);
                if (option) {
                  if (event.target.checked) {
                    option.selected = "selected";
                  } else {
                    option.selected = "";
                  }
                }
                setOptionValues([...(ref.current?.selectedOptions ?? [])].map(option => option.value));
                ref.current?.dispatchEvent(new Event("change", {
                  bubbles: true
                }));
              }
            })]
          }, childValue);
        })
      })
    }) : null]
  });
};
const Select = /*#__PURE__*/forwardRef(SelectComponent);
Select.displayName = "Select";

// Custom property for the FormField to consume
Select.isInput = true;
export default Select;